import { useMemo, useState, type FormEvent } from "react";

import type {
  Direction,
  Event,
  Operator,
  Scan,
  ScanLogResultCode
} from "@attendik/types/admin-schemas";

import { api } from "../../api/client.js";
import { isApiError } from "../../api/errors.js";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../../components/ui/dialog.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Select } from "../../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table.js";
import { Badge } from "../../components/ui/badge.js";
import { EmptyState, ErrorState, LoadingState } from "../../components/states.js";
import { formatDateTime } from "../../lib/format.js";
import { useAsync } from "../../lib/useAsync.js";
import { useAuth } from "../../store/auth.js";

const PAGE_SIZE = 25;

// "ok" filters to accepted scans (result_code = null); the empty option means
// no filter at all.
type ResultFilter = "" | "ok" | "ALREADY_IN" | "ALREADY_OUT";

/** Scan log tab — read-only paginated scans with filters (build plan P2-T8e). */
export function ScanLogTab({ event }: { event: Event }): JSX.Element {
  const [operatorFilter, setOperatorFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState<Direction | "">("");
  const [resultFilter, setResultFilter] = useState<ResultFilter>("");
  // CR-11. When the event has gone live the log defaults to the live run only;
  // this reveals the pre-go-live test scans. Irrelevant in test mode (the
  // marker is null, so everything shows regardless) — the toggle is hidden then.
  const [showTest, setShowTest] = useState(false);
  const [page, setPage] = useState(1);
  const [emailing, setEmailing] = useState(false);
  const [emailToast, setEmailToast] = useState<{
    kind: "success" | "warning";
    message: string;
  } | null>(null);

  const operators = useAsync(() => api.listEventOperators(event.id), [event.id]);

  const resultCode: ScanLogResultCode | undefined = useMemo(() => {
    if (resultFilter === "") {
      return undefined;
    }
    if (resultFilter === "ok") {
      return null;
    }
    return resultFilter;
  }, [resultFilter]);

  const scans = useAsync(
    () =>
      api.listScans(event.id, {
        operator_id: operatorFilter || undefined,
        direction: directionFilter || undefined,
        result_code: resultCode,
        include_test: showTest || undefined,
        page,
        limit: PAGE_SIZE
      }),
    [event.id, operatorFilter, directionFilter, resultFilter, showTest, page]
  );

  const operatorName = useMemo(() => {
    const map = new Map<string, string>();
    for (const op of operators.data ?? []) {
      map.set(op.id, op.name);
    }
    return map;
  }, [operators.data]);

  const totalPages = scans.data
    ? Math.max(1, Math.ceil(scans.data.total / PAGE_SIZE))
    : 1;

  const exportCsv = (): void => {
    if (!scans.data || scans.data.data.length === 0) {
      return;
    }
    downloadScansCsv(scans.data.data, operatorName, event.name);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="scan-operator" className="text-muted-foreground">
              Operator
            </Label>
            <Select
              id="scan-operator"
              className="w-44"
              value={operatorFilter}
              onChange={(e) => {
                setOperatorFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              {(operators.data ?? []).map((op: Operator) => (
                <option key={op.id} value={op.id}>
                  {op.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="scan-direction" className="text-muted-foreground">
              Direction
            </Label>
            <Select
              id="scan-direction"
              className="w-32"
              value={directionFilter}
              onChange={(e) => {
                setDirectionFilter(e.target.value as Direction | "");
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="ENTER">Enter</option>
              <option value="EXIT">Exit</option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="scan-result" className="text-muted-foreground">
              Result
            </Label>
            <Select
              id="scan-result"
              className="w-36"
              value={resultFilter}
              onChange={(e) => {
                setResultFilter(e.target.value as ResultFilter);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="ok">OK only</option>
              <option value="ALREADY_IN">Already in</option>
              <option value="ALREADY_OUT">Already out</option>
            </Select>
          </div>
          {event.went_live_at ? (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={showTest}
                onChange={(e) => {
                  setShowTest(e.target.checked);
                  setPage(1);
                }}
                data-testid="scan-show-test"
              />
              Show test-period scans
            </label>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={scans.reload}
            disabled={scans.loading}
          >
            {scans.loading ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={!scans.data || scans.data.data.length === 0}
          >
            Export CSV (page)
          </Button>
          <Button onClick={() => setEmailing(true)}>Email scan log</Button>
        </div>
      </div>

      {emailToast ? (
        <p
          role="status"
          className={
            "text-sm " +
            (emailToast.kind === "success"
              ? "text-emerald-700"
              : "text-amber-700")
          }
        >
          {emailToast.message}
        </p>
      ) : null}

      <Card>
        {scans.loading ? (
          <LoadingState />
        ) : scans.error ? (
          <ErrorState message={scans.error} onRetry={scans.reload} />
        ) : !scans.data || scans.data.data.length === 0 ? (
          <EmptyState
            title="No scans"
            description="Scans recorded for this event will appear here."
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scanned at</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Badge ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scans.data.data.map((scan) => (
                  <TableRow key={scan.id}>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(scan.scanned_at)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={scan.direction === "ENTER" ? "success" : "warning"}
                      >
                        {scan.direction}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {scan.result_code ? (
                        <Badge variant="warning">{scan.result_code}</Badge>
                      ) : (
                        <Badge variant="outline">OK</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {operatorName.get(scan.scanned_by) ?? scan.scanned_by}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {scan.badge_id}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between border-t border-border p-3 text-sm">
              <span className="text-muted-foreground">
                {scans.data.total} scan{scans.data.total === 1 ? "" : "s"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {emailing ? (
        <EmailScanLogDialog
          event={event}
          onClose={() => setEmailing(false)}
          onSent={(recipientsCount, warning) => {
            setEmailing(false);
            if (warning) {
              setEmailToast({
                kind: "warning",
                message: `Sent to ${String(
                  recipientsCount
                )} recipient(s) with a warning: ${warning}`
              });
            } else {
              setEmailToast({
                kind: "success",
                message: `Scan log emailed to ${String(recipientsCount)} recipient(s).`
              });
            }
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * "Email scan log" modal. Recipient list starts with the current admin's email
 * (when available); operator may add or remove entries. Submit is disabled
 * until at least one valid email is present.
 */
function EmailScanLogDialog({
  event,
  onClose,
  onSent
}: {
  event: Event;
  onClose: () => void;
  onSent: (recipientsCount: number, warning: string | undefined) => void;
}): JSX.Element {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState<string[]>(
    user?.email ? [user.email] : [""]
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setRecipient = (index: number, value: string): void => {
    setRecipients((prev) => prev.map((r, i) => (i === index ? value : r)));
  };
  const addRecipient = (): void => {
    setRecipients((prev) => (prev.length >= 10 ? prev : [...prev, ""]));
  };
  const removeRecipient = (index: number): void => {
    setRecipients((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)
    );
  };

  // Trim, drop blanks, drop duplicates.
  const cleaned = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const r of recipients) {
      const trimmed = r.trim();
      if (!trimmed) {
        continue;
      }
      const lower = trimmed.toLowerCase();
      if (seen.has(lower)) {
        continue;
      }
      seen.add(lower);
      out.push(trimmed);
    }
    return out;
  }, [recipients]);

  // Tiny RFC-shaped check — server is authoritative.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const allValid =
    cleaned.length > 0 && cleaned.every((r) => EMAIL_RE.test(r));

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!allValid) {
      setError("Enter at least one valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.emailScanLog(event.id, cleaned);
      onSent(result.recipients_count, result.warning ?? undefined);
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not send the email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy="email-scan-log-title">
      <DialogHeader
        id="email-scan-log-title"
        title="Email scan log"
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            Send the scan log report (Standard statistics + a CSV of every
            scan) to up to 10 recipients. Each recipient receives their own
            message — addresses are never disclosed to other recipients.
          </p>
          <div className="space-y-2">
            <Label>Recipients</Label>
            {recipients.map((value, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="email"
                  value={value}
                  placeholder="person@example.com"
                  onChange={(e) => setRecipient(index, e.target.value)}
                  aria-label={`Recipient ${String(index + 1)}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={recipients.length <= 1}
                  onClick={() => removeRecipient(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRecipient}
              disabled={recipients.length >= 10}
            >
              Add recipient
            </Button>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!allValid || submitting}>
            {submitting ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/** Builds a CSV of the current page and triggers a browser download. */
function downloadScansCsv(
  scans: Scan[],
  operatorName: Map<string, string>,
  eventName: string
): void {
  const escape = (value: string): string => `"${value.replace(/"/g, '""')}"`;
  const header = ["scanned_at", "direction", "result_code", "operator", "badge_id"];
  const rows = scans.map((scan) =>
    [
      scan.scanned_at,
      scan.direction,
      scan.result_code ?? "OK",
      operatorName.get(scan.scanned_by) ?? scan.scanned_by,
      scan.badge_id
    ]
      .map((cell) => escape(String(cell)))
      .join(",")
  );
  const csv = [header.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${eventName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-scans.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
